/*
 * Product line / Colour / Fabric filters for collection pages.
 *
 * HAYA sells each colour as its own product, so these filters are built from data attributes
 * rendered on each grid item (see snippets/cntr-filter-attributes.liquid). To filter the whole
 * collection rather than one page, the remaining pages are loaded in via the Section Rendering API
 * and pagination is removed. Works alongside the theme's Availability / Price filters and sorting
 * (facets.js re-renders #ProductGridContainer; we re-apply on every re-render).
 */
(() => {
  if (customElements.get('cntr-collection-filters')) return;

  const GROUPS = [
    { key: 'line', label: 'Product', param: 'line' },
    { key: 'colour', label: 'Colour', param: 'colour' },
    { key: 'fabric', label: 'Fabric', param: 'fabric' },
  ];

  const escapeHtml = (value) =>
    String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  class CntrCollectionFilters extends HTMLElement {
    connectedCallback() {
      // The element is moved between the mobile slot and the desktop filter row; only set up once.
      if (this.initialized) return;
      this.initialized = true;

      this.homeSlot = this.closest('[data-hh-filters-slot]');
      this.activeHost = this.homeSlot?.querySelector('[data-hh-active]');
      this.desktopQuery = window.matchMedia('(min-width: 750px)');
      this.desktopQuery.addEventListener('change', () => {
        this.place();
        this.renderActive();
      });
      this.activeHost?.addEventListener('click', this.onClick.bind(this));

      this.sectionId = this.dataset.sectionId;
      this.selected = new Map(GROUPS.map((g) => [g.key, new Set(this.readParam(g.param))]));
      this.openGroup = null;
      this.countTexts = null;

      const colourGroup = GROUPS.find((g) => g.key === 'colour');
      if (this.dataset.colourWord) colourGroup.label = this.dataset.colourWord;

      this.addEventListener('click', this.onClick.bind(this));
      document.addEventListener('click', (event) => {
        if (this.openGroup && !this.contains(event.target)) this.toggleGroup(null);
        // Opening one of our pills closes the theme's Availability / Price popups, and vice versa.
        if (this.contains(event.target) && event.target.closest('[data-toggle]')) {
          document.querySelectorAll('#FacetFiltersForm details[open]').forEach((d) => d.removeAttribute('open'));
        }
      });
      document.addEventListener(
        'toggle',
        (event) => {
          if (event.target.matches?.('#FacetFiltersForm details') && event.target.open && this.openGroup) this.toggleGroup(null);
        },
        true
      );
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.openGroup) this.toggleGroup(null);
      });

      this.place();
      this.container = document.getElementById('ProductGridContainer');
      this.observer = new MutationObserver(() => this.refresh());
      if (this.container) this.observer.observe(this.container, { childList: true });

      this.refresh();
    }

    /*
     * Sit inside the theme's filter row, first in line (before Availability / Price), at every width.
     * On mobile the theme's Sort control also moves into that row so everything is one swipeable line.
     */
    place() {
      const row = document.getElementById('FacetsWrapperDesktop');
      if (!row) {
        if (this.homeSlot && this.parentElement !== this.homeSlot) this.homeSlot.prepend(this);
        return;
      }
      if (this.parentElement !== row) row.prepend(this);
      this.classList.add('hh-filters--inline');

      const form = row.closest('form');
      if (!row.dataset.hhFade) {
        // Toggle the right-edge fade (mobile) once the row is scrolled to the end.
        row.dataset.hhFade = 'true';
        const updateFade = () => form?.classList.toggle('hh-scrolled-end', row.scrollLeft + row.clientWidth >= row.scrollWidth - 2);
        row.addEventListener('scroll', updateFade, { passive: true });
        // Re-check whenever the row or its contents change size (fonts, pills added, re-renders).
        const resizeObserver = new ResizeObserver(updateFade);
        resizeObserver.observe(row);
        [...row.children].forEach((child) => resizeObserver.observe(child));
        window.addEventListener('load', updateFade);
        updateFade();
      }
      const sort = form?.querySelector('.sorting');
      if (!sort) return;
      if (!this.desktopQuery.matches) {
        if (sort.parentElement !== row) row.appendChild(sort);
      } else if (sort.parentElement === row) {
        row.after(sort);
      }
    }

    readParam(name) {
      const value = new URLSearchParams(window.location.search).get(name);
      return value ? value.split(',').filter(Boolean) : [];
    }

    writeParams() {
      const params = new URLSearchParams(window.location.search);
      GROUPS.forEach((g) => {
        const values = [...this.selected.get(g.key)];
        if (values.length) params.set(g.param, values.join(','));
        else params.delete(g.param);
      });
      const query = params.toString();
      const url = `${window.location.pathname}${query ? `?${query}` : ''}`;
      window.history.replaceState(window.history.state, '', url);
    }

    /* ---------------------------------------------------------------- */
    /* Loading every page so filters cover the whole collection          */
    /* ---------------------------------------------------------------- */

    async refresh() {
      if (this.refreshing) return;
      this.refreshing = true;
      this.observer.disconnect();
      this.countTexts = null;

      try {
        await this.loadAllPages();
      } catch (error) {
        console.warn('[cntr-filters] could not load all pages', error);
      }

      this.items = [...document.querySelectorAll('#product-grid [data-hh-item]')];
      this.buildOptions();
      this.render();
      this.apply();
      // facets.js rewrites the URL when sorting / using its own filters; keep ours in it.
      this.writeParams();

      if (this.container) this.observer.observe(this.container, { childList: true });
      this.refreshing = false;
    }

    async loadAllPages() {
      const grid = document.getElementById('product-grid');
      const pagination = this.container?.querySelector('.pagination-wrapper, .pagination');
      if (!grid || !pagination || grid.dataset.hhAllLoaded) return;

      const pageNumbers = [...pagination.querySelectorAll('a[href*="page="]')]
        .map((a) => Number(new URL(a.href, window.location.origin).searchParams.get('page')))
        .filter(Boolean);
      const lastPage = Math.max(1, ...pageNumbers);
      const currentPage = Number(new URLSearchParams(window.location.search).get('page')) || 1;
      if (lastPage <= 1) return;

      this.classList.add('is-loading');
      const params = new URLSearchParams(window.location.search);
      GROUPS.forEach((g) => params.delete(g.param));
      params.set('section_id', this.sectionId);

      const pages = [];
      for (let page = 1; page <= lastPage; page += 1) {
        if (page === currentPage) continue;
        params.set('page', page);
        pages.push(
          fetch(`${window.location.pathname}?${params.toString()}`)
            .then((response) => response.text())
            .then((html) => ({ page, html }))
        );
      }

      const results = (await Promise.all(pages)).sort((a, b) => a.page - b.page);
      const before = results.filter((r) => r.page < currentPage);
      const after = results.filter((r) => r.page > currentPage);
      const parse = (html) =>
        [...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('#product-grid > li')];

      const firstItem = grid.firstElementChild;
      before.forEach(({ html }) => parse(html).forEach((li) => grid.insertBefore(li, firstItem)));
      after.forEach(({ html }) => parse(html).forEach((li) => grid.appendChild(li)));

      pagination.closest('.pagination-wrapper')?.remove();
      pagination.remove();
      grid.dataset.hhAllLoaded = 'true';
      this.classList.remove('is-loading');
    }

    /* ---------------------------------------------------------------- */
    /* Options + filtering                                               */
    /* ---------------------------------------------------------------- */

    buildOptions() {
      this.options = new Map();
      GROUPS.forEach((g) => {
        const values = new Map();
        this.items.forEach((item) => {
          const value = item.dataset[g.key];
          if (!value) return;
          if (!values.has(value)) values.set(value, { value, swatch: g.key === 'colour' ? item.dataset.swatch || '' : '' });
        });
        const list = [...values.values()].sort((a, b) => a.value.localeCompare(b.value));
        this.options.set(g.key, list);

        // Drop selections that no longer exist (e.g. after an availability filter).
        const selected = this.selected.get(g.key);
        [...selected].forEach((v) => {
          if (!values.has(v)) selected.delete(v);
        });
      });
    }

    matches(item, skipKey = null) {
      return GROUPS.every((g) => {
        if (g.key === skipKey) return true;
        const selected = this.selected.get(g.key);
        return !selected.size || selected.has(item.dataset[g.key]);
      });
    }

    countFor(groupKey, value) {
      return this.items.filter((item) => item.dataset[groupKey] === value && this.matches(item, groupKey)).length;
    }

    apply() {
      let visible = 0;
      this.items.forEach((item) => {
        const show = this.matches(item);
        item.hidden = !show;
        if (show) visible += 1;
      });

      const active = GROUPS.some((g) => this.selected.get(g.key).size);
      this.updateCount(active ? visible : null);
      this.toggleEmpty(active && visible === 0);
      this.renderCounts();
      this.renderActive();
    }

    updateCount(visible) {
      const targets = ['ProductCount', 'ProductCountDesktop'].map((id) => document.getElementById(id)).filter(Boolean);
      if (!this.countTexts) this.countTexts = targets.map((el) => el.textContent);
      targets.forEach((el, i) => {
        el.textContent = visible === null ? this.countTexts[i] : `${visible} product${visible === 1 ? '' : 's'}`;
      });
    }

    toggleEmpty(show) {
      let empty = document.getElementById('HhFiltersEmpty');
      if (!show) {
        empty?.remove();
        return;
      }
      if (empty) return;
      empty = document.createElement('div');
      empty.id = 'HhFiltersEmpty';
      empty.className = 'hh-filters__empty';
      empty.innerHTML = 'No products match these filters. <button type="button" data-hh-clear>Clear filters</button>';
      document.getElementById('product-grid')?.after(empty);
      empty.querySelector('[data-hh-clear]').addEventListener('click', () => this.clearAll());
    }

    /* ---------------------------------------------------------------- */
    /* UI                                                                */
    /* ---------------------------------------------------------------- */

    render() {
      const groups = GROUPS.filter((g) => this.options.get(g.key).length > 1);
      if (!groups.length) {
        this.innerHTML = '';
        return;
      }

      this.innerHTML = `
        <div class="hh-filters__bar" role="group" aria-label="Filter products">
          ${groups
            .map(
              (g) => `
            <div class="hh-filters__group" data-group="${g.key}">
              <button type="button" class="hh-filters__toggle" data-toggle="${g.key}" aria-expanded="false" aria-controls="HhFilter-${g.key}">
                <span>${escapeHtml(g.label)}</span>
                <span class="hh-filters__badge" data-badge="${g.key}" hidden></span>
                <svg viewBox="0 0 10 6" aria-hidden="true"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <div class="hh-filters__panel${g.key === 'colour' ? ' hh-filters__panel--grid' : ''}" id="HhFilter-${g.key}" hidden>
                ${this.options
                  .get(g.key)
                  .map(
                    (o) => `
                  <button type="button" class="hh-filters__option" role="checkbox" aria-checked="false" data-group="${g.key}" data-value="${escapeHtml(o.value)}">
                    <span class="hh-filters__check" aria-hidden="true"></span>
                    ${o.swatch ? `<span class="hh-filters__swatch" style="--hh-swatch: ${escapeHtml(o.swatch)}" aria-hidden="true"></span>` : ''}
                    <span class="hh-filters__label">${escapeHtml(o.value)}</span>
                    <span class="hh-filters__count" data-count></span>
                  </button>`
                  )
                  .join('')}
              </div>
            </div>`
            )
            .join('')}
        </div>
`;
    }

    renderCounts() {
      this.querySelectorAll('.hh-filters__option').forEach((button) => {
        const { group, value } = button.dataset;
        const checked = this.selected.get(group).has(value);
        const count = this.countFor(group, value);
        button.setAttribute('aria-checked', String(checked));
        // Hide options with no matches (given the other active filters) to keep panels short.
        button.hidden = !checked && count === 0;
        button.querySelector('[data-count]').textContent = count;
      });
      GROUPS.forEach((g) => {
        const badge = this.querySelector(`[data-badge="${g.key}"]`);
        if (!badge) return;
        const size = this.selected.get(g.key).size;
        badge.hidden = size === 0;
        badge.textContent = size;
      });
    }

    renderActive() {
      const chips = [];
      GROUPS.forEach((g) => {
        this.selected.get(g.key).forEach((value) => {
          chips.push(
            `<button type="button" class="hh-filters__chip" data-hh-chip data-remove-group="${g.key}" data-remove-value="${escapeHtml(value)}">${escapeHtml(value)}<span aria-hidden="true">×</span><span class="visually-hidden">Remove filter</span></button>`
          );
        });
      });

      // Desktop: share the theme's active-filter row (In stock, price…) and its single "Clear all".
      const themeRow = document.querySelector('#FacetFiltersForm .active-facets-desktop');
      if (themeRow) {
        this.activeHost.hidden = true;
        this.activeHost.innerHTML = '';
        this.themeRowObserver?.disconnect();
        themeRow.querySelectorAll('[data-hh-chip]').forEach((chip) => chip.remove());
        const clearAll = themeRow.querySelector('.active-facets__button-wrapper');
        clearAll?.insertAdjacentHTML('beforebegin', chips.join(''));
        themeRow.classList.toggle('hh-has-chips', chips.length > 0);
        if (!this.themeRowBound) {
          this.themeRowBound = true;
          themeRow.addEventListener('click', (event) => {
            if (event.target.closest('[data-hh-chip]')) this.onClick(event);
            // The theme's "Clear all" clears ours too.
            else if (event.target.closest('.active-facets__button-remove')) this.clearAll({ render: false });
          });
        }
        // facets.js replaces this row's HTML after its own filters change; put our chips back.
        this.themeRowObserver ||= new MutationObserver(() => this.renderActive());
        this.themeRowObserver.observe(themeRow, { childList: true });
        return;
      }

      const wrap = this.activeHost;
      if (!wrap) return;
      wrap.hidden = chips.length === 0;
      wrap.innerHTML = chips.length ? `${chips.join('')}<button type="button" class="hh-filters__clear" data-hh-clear>Clear all</button>` : '';
    }

    toggleGroup(key) {
      this.openGroup = this.openGroup === key ? null : key;
      this.querySelectorAll('.hh-filters__group').forEach((group) => {
        const open = group.dataset.group === this.openGroup;
        group.classList.toggle('is-open', open);
        group.querySelector('.hh-filters__toggle').setAttribute('aria-expanded', String(open));
        group.querySelector('.hh-filters__panel').hidden = !open;
      });
    }

    onClick(event) {
      const toggle = event.target.closest('[data-toggle]');
      if (toggle) {
        this.toggleGroup(toggle.dataset.toggle);
        return;
      }

      const option = event.target.closest('.hh-filters__option');
      if (option && !option.disabled) {
        const set = this.selected.get(option.dataset.group);
        const value = option.dataset.value;
        if (set.has(value)) set.delete(value);
        else set.add(value);
        this.writeParams();
        this.apply();
        return;
      }

      const chip = event.target.closest('[data-remove-group]');
      if (chip) {
        this.selected.get(chip.dataset.removeGroup).delete(chip.dataset.removeValue);
        this.writeParams();
        this.apply();
        return;
      }

      if (event.target.closest('[data-hh-clear]')) this.clearAll();
    }

    clearAll({ render = true } = {}) {
      this.selected.forEach((set) => set.clear());
      this.writeParams();
      if (render) this.apply();
    }
  }

  customElements.define('cntr-collection-filters', CntrCollectionFilters);
})();
